import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
  useMemo,
  memo,
} from 'react';
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import utc from 'dayjs/plugin/utc';

import Text from '../Text/Text';
import { TimerProps } from './Timer.types';
import { getFormat, getInterval } from './Timer.utils';
import { useTranslation } from '@betty/i18n';
import { SECOND_MILLISECOND } from './Timer.constants';

// Extend necessary plugins
dayjs.extend(duration);
dayjs.extend(utc);

function Timer({
  time,
  onCountDownComplete,
  unitSize = 'short',
  displayMode = 'precise',
  ...rest
}: TimerProps) {
  const { t } = useTranslation();
  const intervalIDRef = useRef<NodeJS.Timeout | null>(null);

  const targetTime = useMemo(() => {
    if (typeof time === 'number') {
      return dayjs.unix(time);
    } else {
      return dayjs(time);
    }
  }, [time]);

  const [currentTime, setCurrentTime] = useState(dayjs());
  const [countDownInterval, setCountDownInterval] = useState(
    getInterval(targetTime),
  );

  const [isTimerComplete, setIsTimerComplete] = useState(false);

  const timeBetween = useMemo(() => {
    const timeDiff = targetTime.diff(currentTime);
    return dayjs.duration(Math.max(timeDiff, 0));
  }, [currentTime, targetTime]);

  const formattedValue = useMemo(
    () => getFormat(timeBetween, unitSize, displayMode, t),
    [timeBetween, unitSize, displayMode, t],
  );

  const intervalCallback = useCallback(() => {
    setCurrentTime(() => {
      if (targetTime.diff(dayjs()) < 0 && intervalIDRef.current) {
        clearInterval(intervalIDRef.current);
        setIsTimerComplete(true);
      }

      return dayjs();
    });
  }, [targetTime]);

  useEffect(() => {
    if (countDownInterval === SECOND_MILLISECOND) {
      return;
    }

    setCountDownInterval(getInterval(targetTime));
  }, [countDownInterval, targetTime, timeBetween]);

  useEffect(() => {
    if (isTimerComplete) {
      onCountDownComplete?.();
    }
  }, [isTimerComplete, onCountDownComplete]);

  useEffect(() => {
    intervalIDRef.current = setInterval(intervalCallback, countDownInterval);

    return () => {
      if (intervalIDRef.current) {
        clearInterval(intervalIDRef.current);
      }
    };
  }, [countDownInterval, intervalCallback]);

  return <Text {...rest}>{formattedValue}</Text>;
}

export default memo(Timer);
